import type { BillingPeriod } from '@pulsify/shared/dto'
import { createContext, useContext, useMemo } from 'react'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useActiveProfileId } from '~/entities/profile/model/use-profiles'
import type {
  PendingChange,
  SubscriptionLimits,
  SubscriptionStatusValue,
  SubscriptionTier,
  SubscriptionUsage,
} from '~/entities/subscription/api'
import { useSubscriptionStatus } from '~/entities/subscription/model/use-stripe'
import { planLimits } from '~/entities/subscription/plans'
import { useAdminBypass } from './admin-bypass'

interface EnrichedLimits extends SubscriptionLimits {
  canRecurEvents: boolean
}

const DEFAULT_LIMITS: SubscriptionLimits = {
  maxActiveEvents: 0,
  maxDraftEvents: 0,
  maxAdvancePublishDays: 0,
}

function enrichLimits(serverLimits: SubscriptionLimits, tier: SubscriptionTier): EnrichedLimits {
  return { ...serverLimits, canRecurEvents: planLimits[tier].canRecurEvents }
}

const UNLIMITED_LIMITS: EnrichedLimits = {
  maxActiveEvents: -1,
  maxDraftEvents: -1,
  maxAdvancePublishDays: -1,
  canRecurEvents: true,
}

interface PlanContextValue {
  tier: SubscriptionTier
  status: SubscriptionStatusValue
  billingPeriod: BillingPeriod | null
  isTrial: boolean
  trialEndsAt: string | null
  pendingChange: PendingChange | null
  isCancelPending: boolean
  limits: EnrichedLimits
  usage: SubscriptionUsage
  isLoading: boolean
  isError: boolean
  isAdminBypassing: boolean
  /** The plan shown is the active profile's own, not the organization's it falls back to. */
  ownPlan: boolean
  billingEntityId: string | null
}

const PlanContext = createContext<PlanContextValue | null>(null)

export function PlanProvider({ children }: { children: React.ReactNode }) {
  const { data: business } = useCurrentBusiness()
  const profileId = useActiveProfileId(business?.id)
  const {
    data: subscription,
    isLoading,
    isError,
  } = useSubscriptionStatus(business?.id ?? '', profileId)
  const { isBypassing } = useAdminBypass()

  const value = useMemo<PlanContextValue>(() => {
    const tier = subscription?.tier ?? 'free'
    return {
      tier,
      status: subscription?.status ?? 'Inactive',
      billingPeriod: subscription?.billingPeriod ?? null,
      isTrial: subscription?.status === 'Trial',
      trialEndsAt: subscription?.trialEndsAt ?? null,
      pendingChange: subscription?.pendingChange ?? null,
      isCancelPending:
        subscription?.pendingChange?.targetTier === 'free' && subscription?.status !== 'Trial',
      limits: isBypassing
        ? UNLIMITED_LIMITS
        : enrichLimits(subscription?.limits ?? DEFAULT_LIMITS, tier),
      usage: subscription?.usage ?? {
        activeEvents: 0,
        draftEvents: 0,
        listings: 0,
      },
      isLoading,
      isError: isError && !subscription,
      isAdminBypassing: isBypassing,
      ownPlan: subscription?.source === 'profile',
      billingEntityId: subscription?.billingEntityId ?? null,
    }
  }, [subscription, isLoading, isError, isBypassing])

  return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>
}

export function usePlan(): PlanContextValue {
  const ctx = useContext(PlanContext)
  if (!ctx) {
    throw new Error('usePlan must be used within a PlanProvider')
  }
  return ctx
}
